// Pure helpers for the Criterion rule tree. Kept free of React so the editor's logic is
// unit-testable without rendering anything — this is the piece most likely to break the
// published snapshot, since SQL, Swift and this file all have to agree on the encoding.

import type { Criterion, Uuid } from './types';

export type CriterionKind =
    | 'always' | 'personalBest' | 'totalVolume'
    | 'sessionsCompleted' | 'completedSession'
    | 'allOf' | 'anyOf' | 'not';

export const CRITERION_KINDS: { kind: CriterionKind; label: string; group: 'leaf' | 'combinator' }[] = [
    { kind: 'always', label: 'Always available', group: 'leaf' },
    { kind: 'completedSession', label: 'Completed a specific session', group: 'leaf' },
    { kind: 'sessionsCompleted', label: 'Completed N sessions', group: 'leaf' },
    { kind: 'personalBest', label: 'Personal best reaches…', group: 'leaf' },
    { kind: 'totalVolume', label: 'Accumulated volume reaches…', group: 'leaf' },
    { kind: 'allOf', label: 'All of…', group: 'combinator' },
    { kind: 'anyOf', label: 'Any of…', group: 'combinator' },
    { kind: 'not', label: 'Not…', group: 'combinator' },
];

export function kindOf(criterion: Criterion): CriterionKind {
    return Object.keys(criterion)[0] as CriterionKind;
}

/** Criteria that read logged performance, and so need the exercise to be `.recorded` somewhere. */
export function isPerformanceKind(kind: CriterionKind): boolean {
    return kind === 'personalBest' || kind === 'totalVolume';
}

export function emptyCriterion(kind: CriterionKind, firstExercise?: Uuid, firstSession?: Uuid): Criterion {
    switch (kind) {
        case 'always':
            return { always: {} };
        case 'personalBest':
            return { personalBest: { _0: firstExercise ?? '', _1: 'repetitions', atLeast: 10 } };
        case 'totalVolume':
            return { totalVolume: { _0: firstExercise ?? '', _1: 'repetitions', atLeast: 100, within: {} } };
        case 'sessionsCompleted':
            return { sessionsCompleted: { atLeast: 5, program: null } };
        case 'completedSession':
            return { completedSession: { _0: firstSession ?? '' } };
        case 'allOf':
            return { allOf: { _0: [{ always: {} }] } };
        case 'anyOf':
            return { anyOf: { _0: [{ always: {} }] } };
        case 'not':
            return { not: { _0: { always: {} } } };
    }
}

/** Child criteria of a combinator; empty for leaves. */
export function childrenOf(criterion: Criterion): Criterion[] {
    if ('allOf' in criterion) return criterion.allOf._0;
    if ('anyOf' in criterion) return criterion.anyOf._0;
    if ('not' in criterion) return [criterion.not._0];
    return [];
}

export function withChildren(criterion: Criterion, children: Criterion[]): Criterion {
    if ('allOf' in criterion) return { allOf: { _0: children } };
    if ('anyOf' in criterion) return { anyOf: { _0: children } };
    if ('not' in criterion) return { not: { _0: children[0] ?? { always: {} } } };
    return criterion;
}

/**
 * Replace the node at `path` (indices from the root) with `next`, returning a new tree.
 * Immutable so React state updates cleanly and undo is possible later.
 */
export function replaceAt(root: Criterion, path: number[], next: Criterion): Criterion {
    if (path.length === 0) return next;
    const [head, ...rest] = path;
    const children = childrenOf(root).slice();
    const target = children[head!];
    if (target === undefined) return root;
    children[head!] = replaceAt(target, rest, next);
    return withChildren(root, children);
}

/** Remove the node at `path`. A combinator left with no children collapses to `always`. */
export function removeAt(root: Criterion, path: number[]): Criterion {
    if (path.length === 0) return { always: {} };
    const [head, ...rest] = path;
    const children = childrenOf(root).slice();
    if (rest.length === 0) {
        children.splice(head!, 1);
        if (children.length === 0) return { always: {} };
        return withChildren(root, children);
    }
    const target = children[head!];
    if (target === undefined) return root;
    children[head!] = removeAt(target, rest);
    return withChildren(root, children);
}

/** Append a child to the combinator at `path`. */
export function appendChild(root: Criterion, path: number[], child: Criterion): Criterion {
    const target = nodeAt(root, path);
    if (!target) return root;
    // `not` holds exactly one child, so appending would silently drop it.
    if ('not' in target) return root;
    return replaceAt(root, path, withChildren(target, [...childrenOf(target), child]));
}

export function nodeAt(root: Criterion, path: number[]): Criterion | null {
    let current: Criterion | undefined = root;
    for (const index of path) {
        if (!current) return null;
        current = childrenOf(current)[index];
    }
    return current ?? null;
}

/** Exercises this rule reads *performance* from — mirrors Criterion.performanceDependencies. */
export function performanceDependencies(criterion: Criterion): Uuid[] {
    if ('personalBest' in criterion) return [criterion.personalBest._0];
    if ('totalVolume' in criterion) return [criterion.totalVolume._0];
    return childrenOf(criterion).flatMap(performanceDependencies);
}

export function sessionDependencies(criterion: Criterion): Uuid[] {
    if ('completedSession' in criterion) return [criterion.completedSession._0];
    return childrenOf(criterion).flatMap(sessionDependencies);
}

/** A leaf still pointing at nothing — blocks a publish, so warn while editing. */
export function hasUnsetReferences(criterion: Criterion): boolean {
    if ('personalBest' in criterion) return !criterion.personalBest._0;
    if ('totalVolume' in criterion) return !criterion.totalVolume._0;
    if ('completedSession' in criterion) return !criterion.completedSession._0;
    return childrenOf(criterion).some(hasUnsetReferences);
}

/** Plain-English rendering, matching how the app phrases requirements to athletes. */
export function describe(
    criterion: Criterion,
    exerciseName: (id: Uuid) => string,
    sessionName: (id: Uuid) => string,
): string {
    if ('always' in criterion) return 'Available from the start';
    if ('personalBest' in criterion) {
        const c = criterion.personalBest;
        return `Reach ${c.atLeast} ${c._1} on ${exerciseName(c._0)}`;
    }
    if ('totalVolume' in criterion) {
        const c = criterion.totalVolume;
        return `Accumulate ${c.atLeast} ${c._1} of ${exerciseName(c._0)}`;
    }
    if ('sessionsCompleted' in criterion) {
        return `Complete ${criterion.sessionsCompleted.atLeast} training sessions`;
    }
    if ('completedSession' in criterion) {
        return `Complete ${sessionName(criterion.completedSession._0)}`;
    }
    if ('allOf' in criterion) {
        return criterion.allOf._0.map((c) => describe(c, exerciseName, sessionName)).join(' and ');
    }
    if ('anyOf' in criterion) {
        return criterion.anyOf._0.map((c) => describe(c, exerciseName, sessionName)).join(' or ');
    }
    return `Not: ${describe(criterion.not._0, exerciseName, sessionName)}`;
}
